import { z } from 'zod';
import { idSchema, isoDateTimeSchema } from './common.js';
import { deploymentBackupStatusSchema } from './backup-evidence.js';
export const operationsCheckStatusSchema = z.enum(['up', 'warning', 'down', 'disabled']);
export const operationsCheckSchema = z.object({
    status: operationsCheckStatusSchema,
    ageSeconds: z.number().min(0).nullable().optional(),
    count: z.number().int().min(0).optional(),
});
export const operationsStatusSchema = z.object({
    status: z.enum(['ok', 'degraded']),
    environment: z.string(),
    collectedAt: isoDateTimeSchema,
    checks: z.object({
        database: operationsCheckSchema,
        redis: operationsCheckSchema,
        worker: operationsCheckSchema,
        queues: operationsCheckSchema,
        backup: operationsCheckSchema,
        offsiteSnapshot: operationsCheckSchema,
        restoreDrill: operationsCheckSchema,
        smtp: operationsCheckSchema,
        dms: operationsCheckSchema,
        ollama: operationsCheckSchema,
        whisper: operationsCheckSchema,
        openBanking: operationsCheckSchema,
        host: operationsCheckSchema,
    }),
    queueBacklog: z.number().int().min(0),
    failedJobs: z.number().int().min(0),
    demoDataDetected: z.boolean(),
    backup: deploymentBackupStatusSchema,
});
export const OPERATIONS_ALERT_DEFAULTS = {
    enabled: false,
    recipients: [],
    minimumIntervalMinutes: 15,
    maxPerMinute: 1,
    maxPerHour: 3,
    maxPerDay: 10,
    repeatIntervalMinutes: 0,
};
export const OPERATIONS_ALERT_TIME_WINDOWS = {
    minute: 60_000,
    hour: 3_600_000,
    day: 86_400_000,
};
export const OPERATIONS_ALERT_MAX_RECIPIENTS = 20;
export const operationsAlertSettingsFieldsSchema = z.object({
    enabled: z.boolean(),
    recipients: z
        .array(z.string().trim().toLowerCase().email().max(254))
        .max(OPERATIONS_ALERT_MAX_RECIPIENTS)
        .refine((entries) => new Set(entries).size === entries.length, 'operationsAlerts.validation.duplicates'),
    minimumIntervalMinutes: z.number().int().min(0).max(10_080),
    maxPerMinute: z.number().int().min(1).max(60),
    maxPerHour: z.number().int().min(1).max(1_000),
    maxPerDay: z.number().int().min(1).max(10_000),
    repeatIntervalMinutes: z.number().int().min(0).max(10_080),
    version: z.number().int().min(0),
});
export const operationsAlertSettingsUpdateSchema = operationsAlertSettingsFieldsSchema
    .strict()
    .superRefine((value, ctx) => {
    if (value.enabled && value.recipients.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['recipients'],
            message: 'operationsAlerts.validation.recipientsRequired',
        });
    }
    if (value.repeatIntervalMinutes > 0 &&
        value.repeatIntervalMinutes < value.minimumIntervalMinutes) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['repeatIntervalMinutes'],
            message: 'operationsAlerts.validation.repeatInterval',
        });
    }
});
export const operationsAlertSettingsSchema = operationsAlertSettingsFieldsSchema.extend({
    updatedAt: isoDateTimeSchema.nullable(),
    smtpConfigured: z.boolean(),
});
export const operationsAlertEventTypeSchema = z.enum(['DEGRADED', 'RECOVERED', 'REMINDER']);
export const operationsAlertDeliveryStateSchema = z.enum([
    'DISABLED',
    'PENDING',
    'LIMITED',
    'SUPERSEDED',
    'SENDING',
    'SENT',
    'FAILED',
    'UNCERTAIN',
]);
export const operationsAlertSnapshotSchema = z.object({
    status: operationsStatusSchema.shape.status,
    checks: z.record(operationsCheckStatusSchema),
    queueBacklog: z.number().int().min(0),
    failedJobs: z.number().int().min(0),
    demoDataDetected: z.boolean(),
});
export const operationsAlertSchema = z.object({
    id: idSchema,
    event: operationsAlertEventTypeSchema,
    environment: z.string(),
    occurredAt: isoDateTimeSchema,
    snapshot: operationsAlertSnapshotSchema,
    deliveryState: operationsAlertDeliveryStateSchema,
    limitReason: z.enum(['INTERVAL', 'MINUTE', 'HOUR', 'DAY']).nullable(),
    sentAt: isoDateTimeSchema.nullable(),
});
export const operationsAlertListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(25),
    event: operationsAlertEventTypeSchema.optional(),
    deliveryState: operationsAlertDeliveryStateSchema.optional(),
});
export const operationsAlertListSchema = z.object({
    data: z.array(operationsAlertSchema),
    meta: z.object({
        page: z.number(),
        pageSize: z.number(),
        total: z.number(),
        totalPages: z.number(),
    }),
});
//# sourceMappingURL=operations-monitoring.js.map